import type { FC, PropsWithChildren } from 'react';
import { useTheme } from 'styled-components';
import { ProConfigProvider } from '@ant-design/pro-provider';
import { ConfigProvider } from 'antd';
import type { ThemeConfig } from 'antd/lib';

export const AppAntConfigProvider: FC<PropsWithChildren> = ({ children }) => {
  const theme = useTheme();

  const PRIMARY_THEME_TOKEN = {
    borderRadius: 4,
    // Font properties
    fontFamily: theme.font.typography.bodyText.fontFamily,
    fontWeightStrong: 500,
    // Colors
    colorPrimary: theme.color.theme.primary,
    colorLink: theme.color.theme.primary,
    // Focus state outline
    lineWidthFocus: 0,
    lineWidth: 1,
    // Disabled state
    colorBgContainerDisabled: theme.color.semantic.primary.background.disabled,
    colorTextDisabled: theme.color.semantic.primary.type.disabled,
  } satisfies ThemeConfig['token'];

  // Shared tokens for input-like components (Input, InputNumber, TextArea, DatePicker).
  // These replace the custom border / placeholder / suffix styling that was previously
  // implemented via styled-components.
  const inputLikeTokens = {
    colorBorder: theme.color.semantic.border.input,
    hoverBorderColor: theme.color.semantic.border.inputHover,
    activeBorderColor: theme.color.semantic.border.input,
    activeShadow: `0 0 0 2px ${theme.color.semantic.focus.border}`,
    colorText: theme.color.semantic.type.primary,
    colorTextPlaceholder: theme.color.semantic.type.secondary,
    colorTextQuaternary: theme.color.semantic.type.secondary,
    addonBg: theme.color.grey.white,
    colorError: theme.color.semantic.border.error,
    colorErrorBorderHover: theme.color.semantic.border.error,
    errorActiveShadow: `0 0 0 2px ${theme.color.semantic.border.error}`,
    colorWarning: theme.color.semantic.feedback.type.warning,
    colorWarningBorderHover: theme.color.semantic.feedback.type.warning,
    warningActiveShadow: `0 0 0 2px ${theme.color.semantic.feedback.type.warning}`,
    paddingInline: 8,
  };

  // Shared tokens for select-like components (Select, TreeSelect).
  // Select uses CSS variables and reads `colorBorderDisabled` for the disabled border.
  const selectLikeTokens = {
    colorBorder: theme.color.semantic.border.input,
    hoverBorderColor: theme.color.semantic.border.inputHover,
    activeBorderColor: theme.color.semantic.border.input,
    activeOutlineColor: theme.color.semantic.focus.border,
    colorBorderDisabled: 'transparent',
    colorText: theme.color.semantic.type.primary,
    colorTextPlaceholder: theme.color.semantic.type.secondary,
    colorTextQuaternary: theme.color.semantic.type.secondary,
    colorError: theme.color.semantic.border.error,
    colorErrorHover: theme.color.semantic.border.error,
    colorErrorOutline: theme.color.semantic.border.error,
    colorWarning: theme.color.semantic.feedback.type.warning,
    colorWarningBorderHover: theme.color.semantic.feedback.type.warning,
    colorWarningOutline: theme.color.semantic.feedback.type.warning,
    paddingSM: 9, // Select derives horizontal padding from paddingSM - lineWidth (9 - 1 = 8px)
  };

  return (
    <ConfigProvider
      theme={{
        token: PRIMARY_THEME_TOKEN,
        hashed: false,
        components: {
          Layout: {
            bodyBg: theme.color.semantic.background.base,
          },
          Form: {
            itemMarginBottom: 16,
          },
          // See https://ant.design/theme-editor
          Alert: {
            colorInfoBg: '#d8e2e0',
            colorInfo: theme.color.theme.primary,
          },
          Button: {
            contentFontSizeSM: 12,
            defaultHoverBg: theme.color.semantic.primary.background.hoverWeak,
            colorPrimary: theme.color.theme.primary,
            colorPrimaryHover: theme.color.theme.light,
            colorPrimaryActive: theme.color.theme.tertiary,
            colorError: theme.color.status.error,
            colorTextDisabled: theme.color.type.disabled,
            colorText: theme.color.theme.primary,
            defaultBg: theme.color.grey.white,
            defaultBorderColor: theme.color.theme.primary,
            defaultActiveBg: theme.color.theme.lighter,
            colorBgContainerDisabled: theme.color.grey.grey4,
          },
          Input: inputLikeTokens,
          InputNumber: inputLikeTokens,
          DatePicker: {
            ...inputLikeTokens,
            cellBgDisabled: 'transparent',
          },
          Select: {
            ...selectLikeTokens,
            optionSelectedColor: theme.color.type.primary,
            optionSelectedBg: theme.color.theme.lighter,
            controlItemBgHover: theme.color.theme.lighterAlt,
            controlItemBgActiveHover: theme.color.grey.grey3,
            colorBgContainerDisabled: theme.color.grey.grey3,
          },
          TreeSelect: {
            ...selectLikeTokens,
            nodeSelectedBg: theme.color.theme.lighter,
            nodeSelectedColor: theme.color.type.primary,
            controlItemBgHover: theme.color.theme.lighterAlt,
            controlItemBgActiveHover: theme.color.grey.grey3,
            colorBgContainerDisabled: theme.color.grey.grey3,
          },
          Switch: {
            colorPrimary: theme.color.semantic.primary.background.default,
            colorPrimaryHover: theme.color.semantic.primary.background.hover,
            colorTextQuaternary: theme.color.semantic.toggle.background.default,
            colorTextTertiary: theme.color.semantic.toggle.background.hover,
          },
          Menu: {
            itemHoverBg: theme.color.theme.lighterAlt,
            itemHoverColor: theme.color.theme.primary,
            itemActiveBg: theme.color.theme.lighterAlt,
            itemSelectedBg: theme.color.theme.lighter,
            colorPrimaryHover: theme.color.theme.primary,
            lineWidthFocus: 2,
          },
          Tooltip: {
            sizePopupArrow: 0,
            borderRadius: 8,
            fontSize: theme.font.size.xs,
            fontWeightStrong: 600,
            colorBgSpotlight: theme.color.grey.white,
            colorText: theme.color.type.primary,
            colorTextLightSolid: theme.color.type.primary,
            colorLink: theme.color.theme.primary,
            boxShadow: '0px 6.4px 14.4px 0px #00000021, 0px 1.2px 3.6px 0px #0000001a',
          },
          Dropdown: {
            controlItemBgHover: theme.color.theme.lighterAlt,
            controlItemBgActiveHover: theme.color.grey.grey3,
          },
          Typography: {
            fontFamily: theme.font.typography.bodyText.fontFamily,
            fontWeightStrong: 500,
          },
          Descriptions: {
            itemPaddingBottom: 16,
            lineHeight: 1,
            fontSize: 14,
            borderRadius: 4,
            controlHeight: 32,
          },
          Timeline: {
            tailColor: theme.color.core.gray.main[300],
          },
          Pagination: {
            lineWidthFocus: 2,
          },
          Table: {
            rowHoverBg: theme.color.grey.grey3,
            rowSelectedBg: theme.color.theme.lighterAlt,
            rowSelectedHoverBg: theme.color.theme.lighter,
          },
          Tour: {
            colorText: theme.color.semantic.primary.type.default,
            colorBgElevated: theme.color.semantic.primary.background.hover,
            borderRadius: 8,
            borderRadiusOuter: 8,
            borderRadiusLG: 8,
            boxShadowTertiary: '0px 0px 14px 2px #25252545, 0px 0px 4px 1px #3233324a',
            boxShadowSecondary: 'none',
          },
          Popover: {
            borderRadius: 8,
            borderRadiusOuter: 8,
            borderRadiusLG: 8,
          },
        },
      }}
    >
      <ProConfigProvider>{children}</ProConfigProvider>
    </ConfigProvider>
  );
};
